import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { Button, Card, Row, Col } from 'react-bootstrap';
import { FiArrowLeft, FiUpload } from 'react-icons/fi';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import DocumentUploadForm from './DocumentUploadForm';
import InternalFooter from '../components/InternalFooter/InternalFooter';
import axiosInstance from '../../services/AxiosInstance';
import { appendDocumentShareToFormData } from '../../utils/documentSharePayload';
import { listStaffByCorporate } from '../../services/StaffService';
import { listVehiclesByCorporate } from '../../services/VehicleService';
import { canUploadDocuments } from '../../utils/documentAccess';
import { isValidDocumentField } from '../../utils/specialCharValidation';
import './commonstyle.css';

const AddDocument = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const folderId = searchParams.get('folderId') ? parseInt(searchParams.get('folderId')) : null;
  const categoryIdFromUrl = searchParams.get('categoryId') ? parseInt(searchParams.get('categoryId')) : null;

  // Upload form state
  const [uploadFiles, setUploadFiles] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [dynamicFields, setDynamicFields] = useState({});
  const [isPrivate, setIsPrivate] = useState(false);
  const [selectedBranches, setSelectedBranches] = useState([]);
  const [selectedDepartments, setSelectedDepartments] = useState([]);
  const [selectedSubDepartments, setSelectedSubDepartments] = useState([]);
  const [branches, setBranches] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [subDepartments, setSubDepartments] = useState([]);
  const [filteredDepartments, setFilteredDepartments] = useState([]);
  const [filteredSubDepartments, setFilteredSubDepartments] = useState([]);
  const [availableUsers, setAvailableUsers] = useState([]);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [documentClassifications, setDocumentClassifications] = useState([]);
  const [loading, setLoading] = useState(false);
  const [hasDocumentAccess, setHasDocumentAccess] = useState(false);
  const [accessLoading, setAccessLoading] = useState(true);
  
  // New document fields
  const [documentTitle, setDocumentTitle] = useState('');
  const [documentRegisteredTo, setDocumentRegisteredTo] = useState('');
  const [documentReferenceNumber, setDocumentReferenceNumber] = useState('');
  const [documentIssueDate, setDocumentIssueDate] = useState('');
  const [documentExpiryDate, setDocumentExpiryDate] = useState('');
  const [remindMeBefore, setRemindMeBefore] = useState('');
  const [documentIssuingAuthority, setDocumentIssuingAuthority] = useState('');
  const [otherComments, setOtherComments] = useState('');
  const [fieldErrors, setFieldErrors] = useState({
    documentTitle: '',
    documentRegisteredTo: '',
    documentReferenceNumber: '',
    documentIssuingAuthority: '',
    otherComments: ''
  });
  
  // Document linking fields
  const [linkToStaff, setLinkToStaff] = useState(false);
  const [staffId, setStaffId] = useState(null);
  const [staffList, setStaffList] = useState([]);
  const [linkToVehicle, setLinkToVehicle] = useState(false);
  const [vehicleId, setVehicleId] = useState(null);
  const [vehicleList, setVehicleList] = useState([]);
  
  // Duplicate handling
  const [duplicateFiles, setDuplicateFiles] = useState([]);
  const [checkingDuplicates, setCheckingDuplicates] = useState(false);
  const [showDuplicateModal, setShowDuplicateModal] = useState(false);
  const [duplicateChoices, setDuplicateChoices] = useState({});

  useEffect(() => {
    checkDocumentAccess();
  }, []);

  const checkDocumentAccess = async () => {
    try {
      setAccessLoading(true);
      const accessCheck = await canUploadDocuments();
      setHasDocumentAccess(accessCheck.canUpload);
      if (!accessCheck.canUpload) {
        setAccessLoading(false);
        return;
      }
      // If access is granted, fetch initial data
      await fetchInitialData();
    } catch (error) {
      console.error('Error checking document access:', error);
      setHasDocumentAccess(false);
    } finally {
      setAccessLoading(false);
    }
  };

  // Fetch staff list when linkToStaff is checked
  useEffect(() => {
    if (linkToStaff && staffList.length === 0) {
      fetchStaffList();
    }
  }, [linkToStaff]);

  // Fetch vehicle list when linkToVehicle is checked
  useEffect(() => {
    if (linkToVehicle && vehicleList.length === 0) {
      fetchVehicleList();
    }
  }, [linkToVehicle]);

  // Prefill classification when categoryId is in URL and classifications are loaded
  useEffect(() => {
    if (categoryIdFromUrl && documentClassifications.length > 0 && !selectedCategory) {
      const categoryExists = documentClassifications.find(cat => cat.id === categoryIdFromUrl);
      if (categoryExists) {
        setSelectedCategory(categoryIdFromUrl);
      }
    }
  }, [categoryIdFromUrl, documentClassifications, selectedCategory]);

  // Fetch folder classification when folderId is provided
  useEffect(() => {
    const fetchFolderClassification = async () => {
      if (!folderId || !documentClassifications.length || selectedCategory || categoryIdFromUrl) return;
      
      try {
        const userData = JSON.parse(localStorage.getItem('user'));
        const corporateId = userData?.corporateId || userData?.id;
        
        // Fetch all folders to find the current folder and check its connection
        const params = new URLSearchParams();
        params.append('corporate_id', corporateId);
        
        const response = await axiosInstance.get(`/documents/list?${params.toString()}`);
        const data = response.data;
        
        if (response.status === 200 && data.success) {
          const responseData = data.data;
          const fetchedFolders = responseData.folders || [];
          
          // Flatten folder tree to find all folders
          const flattenFolderTree = (folderTree) => {
            let allFolders = [];
            const traverse = (folders) => {
              folders.forEach(folder => {
                const { children, ...folderWithoutChildren } = folder;
                allFolders.push(folderWithoutChildren);
                if (folder.children && folder.children.length > 0) {
                  traverse(folder.children);
                }
              });
            };
            traverse(folderTree);
            return allFolders;
          };
          
          const allFolders = flattenFolderTree(fetchedFolders);
          const currentFolder = allFolders.find(f => f.id === folderId);
          
          // Check if folder is connected to a classification
          if (currentFolder) {
            // Check connection_type and connection_id (folder might be connected to classification)
            if (currentFolder.id) {
              const categoryId = parseInt(currentFolder.id);
              const categoryExists = documentClassifications.find(cat => cat.id === categoryId);
              if (categoryExists) {
                setSelectedCategory(categoryId);
                return;
              }
            }
          }
          
          // Alternative: Check documents in the folder to see what classification they have
          const docParams = new URLSearchParams();
          docParams.append('corporate_id', corporateId);
          docParams.append('parent_id', folderId);
          
          const docResponse = await axiosInstance.get(`/documents/list?${docParams.toString()}`);
          const docData = docResponse.data;
          
          if (docResponse.status === 200 && docData.success) {
            const docResponseData = docData.data;
            const documents = docResponseData.documents || [];
            
            // Find the most common classification in this folder
            if (documents.length > 0) {
              const classificationCounts = {};
              documents.forEach(doc => {
                const categoryId = doc.category?.id || doc.category_id;
                if (categoryId) {
                  classificationCounts[categoryId] = (classificationCounts[categoryId] || 0) + 1;
                }
              });
              
              // Get the most common classification
              if (Object.keys(classificationCounts).length > 0) {
                const mostCommonCategoryId = Object.keys(classificationCounts).reduce((a, b) => 
                  classificationCounts[a] > classificationCounts[b] ? a : b
                );
                
                if (mostCommonCategoryId) {
                  const categoryExists = documentClassifications.find(cat => cat.id === parseInt(mostCommonCategoryId));
                  if (categoryExists) {
                    setSelectedCategory(parseInt(mostCommonCategoryId));
                  }
                }
              }
            }
          }
        }
      } catch (error) {
        console.error('Error fetching folder classification:', error);
        // Silently fail - don't show error to user as this is just for prefilling
      }
    };
    
    if (folderId && !categoryIdFromUrl && documentClassifications.length > 0 && !selectedCategory) {
      fetchFolderClassification();
    }
  }, [folderId, documentClassifications, categoryIdFromUrl, selectedCategory]);

  const fetchInitialData = async () => {
    try {
      setLoading(true);
      await Promise.all([
        fetchDocumentClassifications(),
        fetchBranches(),
        fetchDepartments(),
        fetchSubDepartments(),
        fetchUsers()
      ]);
    } catch (error) {
      console.error('Error fetching initial data:', error);
      toast.error('Failed to load initial data');
    } finally {
      setLoading(false);
    }
  };


  const fetchDocumentClassifications = async () => {
    try {
      const userData = JSON.parse(localStorage.getItem('user'));
      const corporateId = userData?.corporateId || userData?.id;
      
      const response = await axiosInstance.get(`/document-categories?corporate_id=${corporateId}`);
      const data = response.data;
      
      // Handle response structure: { success: true, data: [...] }
      let classifications = [];
      if (data && data.success === true && Array.isArray(data.data)) {
        classifications = data.data;
      } else if (data && Array.isArray(data.data)) {
        classifications = data.data;
      } else if (Array.isArray(data)) {
        classifications = data;
      }
      
      setDocumentClassifications(classifications);
      
      // Prefill classification if categoryId is provided in URL
      if (categoryIdFromUrl && classifications.length > 0) {
        const categoryExists = classifications.find(cat => cat.id === categoryIdFromUrl);
        if (categoryExists) {
          setSelectedCategory(categoryIdFromUrl);
        }
      }
    } catch (error) {
      console.error('Error fetching document classifications:', error);
    }
  };

  const fetchBranches = async () => {
    try {
      const userData = JSON.parse(localStorage.getItem('user'));
      const corporateId = userData?.corporateId || userData?.id;
      
      const res = await axiosInstance.get(`/settings/branches?corporate_id=${corporateId}`);
      const data = res.data;
      if (res) {
        setBranches(data.data || data || []);
      }
    } catch (error) {
      console.error('Error fetching branches:', error);
    }
  };

  const fetchDepartments = async () => {
    try {
      const userData = JSON.parse(localStorage.getItem('user'));
      const corporateId = userData?.corporateId || userData?.id;
      
      const res = await axiosInstance.get(`/settings/departments?corporate_id=${corporateId}`);
      const data = res.data;
      if (res) {
        const deptList = data.data || data || [];
        setDepartments(deptList);
        setFilteredDepartments(deptList);
      }
    } catch (error) {
      console.error('Error fetching departments:', error);
    }
  };

  const fetchSubDepartments = async () => {
    try {
      const userData = JSON.parse(localStorage.getItem('user'));
      const corporateId = userData?.corporateId || userData?.id;
      
      let res;
      try {
        res = await axiosInstance.get(`/settings/subDepartments?corporate_id=${corporateId}`);
      } catch (e1) {
        res = await axiosInstance.get(`/settings/subdepartments?corporate_id=${corporateId}`);
      }
      const data = res.data;
      if (res) {
        const subDeptList = data.data || data || [];
        setSubDepartments(subDeptList);
        setFilteredSubDepartments(subDeptList);
      }
    } catch (error) {
      console.error('Error fetching sub-departments:', error);
    }
  };

  const fetchStaffList = async () => {
    try {
      const userData = JSON.parse(localStorage.getItem('user'));
      const corporateId = userData?.corporateId || userData?.id;
      if (!corporateId) return;
      
      const res = await listStaffByCorporate(corporateId, { limit: 1000 });
      if (res.data.success) {
        setStaffList(res.data.data || []);
      }
    } catch (error) {
      console.error('Error fetching staff list:', error);
    }
  };

  const fetchVehicleList = async () => {
    try {
      const userData = JSON.parse(localStorage.getItem('user'));
      const corporateId = userData?.corporateId || userData?.id;
      if (!corporateId) return;
      
      const res = await listVehiclesByCorporate(corporateId, { limit: 1000 });
      if (res.data.success) {
        setVehicleList(res.data.data || []);
      }
    } catch (error) {
      console.error('Error fetching vehicle list:', error);
    }
  };

  const fetchUsers = async () => {
    try {
      const userData = JSON.parse(localStorage.getItem('user'));
      const corporateId = userData?.corporateId || userData?.id;
      
      const params = new URLSearchParams();
      params.append('corporateId', corporateId);
      
      const response = await axiosInstance.get(`/list/corporateuserslist?${params.toString()}`);
      const users = response.data.users || [];
      setAvailableUsers(users);
      setFilteredUsers(users);
    } catch (error) {
      console.error('Error fetching users:', error);
    }
  };

  // Handle branch change - filter departments
  const handleBranchChange = (branchIds, selectedOptions) => {
    setSelectedBranches(branchIds);
    
    if (branchIds.length > 0) {
      const filtered = departments.filter(dept => branchIds.includes(dept.branch_id));
      setFilteredDepartments(filtered);
      
      // Filter selected departments to keep only valid ones
      const validDepartmentIds = selectedDepartments.filter(deptId => {
        return filtered.some(d => d.id === deptId);
      });
      setSelectedDepartments(validDepartmentIds);
      
      // Filter sub-departments based on valid departments
      const subs = subDepartments.filter(sd => validDepartmentIds.includes(sd.department_id));
      setFilteredSubDepartments(subs);
      
      // Keep only valid sub-departments
      const validSubDepartmentIds = selectedSubDepartments.filter(subDeptId => {
        return subs.some(sd => (sd.id || sd.sub_department_id) === subDeptId);
      });
      setSelectedSubDepartments(validSubDepartmentIds);
    } else {
      setFilteredDepartments(departments);
      setFilteredSubDepartments(subDepartments);
    }
  };

  // Handle department change - filter sub-departments
  const handleDepartmentChange = (departmentIds, selectedOptions) => {
    setSelectedDepartments(departmentIds);
    
    if (departmentIds.length > 0) {
      const subs = subDepartments.filter(sd => departmentIds.includes(sd.department_id));
      setFilteredSubDepartments(subs);
      
      // Keep only valid sub-departments
      const validSubDepartmentIds = selectedSubDepartments.filter(subDeptId => {
        return subs.some(sd => (sd.id || sd.sub_department_id) === subDeptId);
      });
      setSelectedSubDepartments(validSubDepartmentIds);
    } else {
      setFilteredSubDepartments(subDepartments);
    }
  };

  // Handle sub-department change
  const handleSubDepartmentChange = (subDepartmentIds, selectedOptions) => {
    setSelectedSubDepartments(subDepartmentIds);
  };

  // Handle user change
  const handleUserChange = (userIds, selectedOptions) => {
    setSelectedUsers(userIds);
  };

  // Check for duplicates
  const checkDuplicates = async (files) => {
    if (!files || files.length === 0) return [];
    
    setCheckingDuplicates(true);
    try {
      const userData = JSON.parse(localStorage.getItem('user'));
      const corporateId = userData?.corporateId || userData?.id;
      
      const fileNames = files.map(f => f.name);
      const params = new URLSearchParams();
      params.append('corporate_id', corporateId);
      params.append('file_names', JSON.stringify(fileNames));
      if (folderId) {
        params.append('folder_id', folderId);
      }
      
      const response = await axiosInstance.get(`/documents/check-duplicates?${params.toString()}`);
      
      if (response.data && response.data.duplicates) {
        return response.data.duplicates;
      }
      return [];
    } catch (error) {
      console.error('Error checking duplicates:', error);
      return [];
    } finally {
      setCheckingDuplicates(false);
    }
  };

  // Process files with user choices for duplicates
  const processFilesWithChoices = (files, choices) => {
    return files.map(file => {
      const duplicate = duplicateFiles.find(d => d.name === file.name);
      if (duplicate && choices[file.name]) {
        return {
          originalFile: file,
          processedFile: file,
          action: choices[file.name],
          wasRenamed: false
        };
      }
      return {
        originalFile: file,
        processedFile: file,
        action: null,
        wasRenamed: false
      };
    });
  };

  const handleFileUpload = async () => {
    if (uploadFiles.length === 0) {
      toast.error('Please select a file to upload');
      return;
    }

    // Check storage space before upload
    const totalFileSize = uploadFiles.reduce((sum, file) => sum + file.size, 0);
    const accessCheck = await canUploadDocuments(totalFileSize);
    
    if (!accessCheck.canUpload) {
      if (accessCheck.reason === 'insufficient_space') {
        toast.error(accessCheck.message || 'Insufficient storage space to upload these files');
      } else {
        toast.error(accessCheck.message || 'You do not have permission to upload documents');
      }
      return;
    }

    // Validate linking fields
    if (linkToStaff && !staffId) {
      toast.error('Please select a staff member when "Link to Staff" is checked');
      return;
    }

    if (linkToVehicle && !vehicleId) {
      toast.error('Please select a vehicle when "Link to Vehicle" is checked');
      return;
    }

    // Special character validation (same as task name in TaskForm)
    let hasError = false;
    const newErrors = { documentTitle: '', documentRegisteredTo: '', documentReferenceNumber: '', documentIssuingAuthority: '', otherComments: '' };
    if (documentTitle.trim() && !isValidDocumentField(documentTitle)) {
      newErrors.documentTitle = 'Document title cannot contain only special characters.';
      hasError = true;
    }
    if (documentRegisteredTo.trim() && !isValidDocumentField(documentRegisteredTo)) {
      newErrors.documentRegisteredTo = 'Cannot contain only special characters.';
      hasError = true;
    }
    if (documentReferenceNumber.trim() && !isValidDocumentField(documentReferenceNumber)) {
      newErrors.documentReferenceNumber = 'Cannot contain only special characters.';
      hasError = true;
    }
    if (documentIssuingAuthority.trim() && !isValidDocumentField(documentIssuingAuthority)) {
      newErrors.documentIssuingAuthority = 'Cannot contain only special characters.';
      hasError = true;
    }
    if (otherComments.trim() && !isValidDocumentField(otherComments)) {
      newErrors.otherComments = 'Cannot contain only special characters.';
      hasError = true;
    }
    if (hasError) {
      setFieldErrors(newErrors);
      return;
    }

    // Validate required dynamic fields only if a classification is selected
    if (selectedCategory) {
      const selectedCategoryData = documentClassifications.find(cat => cat.id === selectedCategory);
      if (selectedCategoryData && selectedCategoryData.fields) {
        const requiredFields = selectedCategoryData.fields.filter(field => field.is_required);
        const missingFields = requiredFields.filter(field => {
          const fieldValue = dynamicFields[field.id];
          return !fieldValue || fieldValue.trim() === '';
        });
        
        if (missingFields.length > 0) {
          toast.error(`Please fill in all required fields: ${missingFields.map(f => f.field_name).join(', ')}`);
          return;
        }
      }
    }

    // Check for duplicates
    const duplicates = await checkDuplicates(uploadFiles);
    
    // If duplicates found, show modal for user to choose
    if (duplicates.length > 0) {
      setDuplicateFiles(duplicates);
      // Initialize choices with default 'copy' for all duplicates
      const initialChoices = {};
      duplicates.forEach(dup => {
        initialChoices[dup.name] = 'copy'; // Default to 'copy'
      });
      setDuplicateChoices(initialChoices);
      setShowDuplicateModal(true);
      return; // Wait for user to make choices
    }

    // No duplicates, proceed with upload
    proceedWithUpload(uploadFiles, {});
  };

  // Proceed with upload after user makes duplicate choices
  const proceedWithUpload = async (files, choices) => {
    setLoading(true);
    
    try {
      const userData = JSON.parse(localStorage.getItem('user'));
      const corporateId = userData?.corporateId || userData?.id;
      
      // Process files based on user choices
      const processedFiles = processFilesWithChoices(files, choices);
      
      // Upload each file
      const uploadPromises = processedFiles.map(async (fileInfo) => {
        try {
          const formData = new FormData();
          
          // Required fields
          formData.append('file', fileInfo.processedFile);
          formData.append('corporation_id', corporateId);
          
          // Optional fields
          if (fileInfo.action) {
            formData.append('duplicate_action', fileInfo.action);
          } else {
            formData.append('duplicate_action', 'copy');
          }
          
          if (folderId) {
            formData.append('document_path_id', folderId);
          }
          
          if (selectedCategory) {
            formData.append('category_id', selectedCategory);
          }
          
          if (Object.keys(dynamicFields).length > 0) {
            formData.append('field_values', JSON.stringify(dynamicFields));
          }
          
          // New document fields
          if (documentTitle) {
            formData.append('document_title', documentTitle);
          }
          if (documentRegisteredTo) {
            formData.append('registered_to', documentRegisteredTo);
          }
          if (documentReferenceNumber) {
            formData.append('reference_number', documentReferenceNumber);
          }
          if (documentIssueDate) {
            formData.append('issue_date', documentIssueDate);
          }
          if (documentExpiryDate) {
            formData.append('expiry_date', documentExpiryDate);
          }
          if (remindMeBefore) {
            formData.append('remind_before', remindMeBefore);
          }
          if (documentIssuingAuthority) {
            formData.append('issuing_authority', documentIssuingAuthority);
          }
          if (otherComments) {
            formData.append('other_comments', otherComments);
          }
          
          if (isPrivate) {
            formData.append('is_private', isPrivate);
          }
          
          // Document linking fields
          formData.append('link_to_staff', linkToStaff ? 'true' : 'false');
          if (linkToStaff && staffId) {
            formData.append('staff_id', staffId);
          }

          formData.append('link_to_vehicle', linkToVehicle ? 'true' : 'false');
          if (linkToVehicle && vehicleId) {
            formData.append('vehicle_id', vehicleId);
          }
          
          // Apply hierarchical sharing rules
          appendDocumentShareToFormData(
            formData,
            selectedBranches,
            selectedDepartments,
            selectedSubDepartments,
            selectedUsers
          );

          // Don't set Content-Type - axios auto-adds multipart/form-data with boundary
          const response = await axiosInstance.post('/documents/upload', formData);

          if (response.status === 200 || response.status === 201) {
            return { success: true, file: fileInfo.originalFile.name };
          } else {
            return { success: false, file: fileInfo.originalFile.name, error: response.data?.message || 'Upload failed' };
          }
        } catch (error) {
          console.error(`Error uploading ${fileInfo.originalFile.name}:`, error);
          return { 
            success: false, 
            file: fileInfo.originalFile.name, 
            error: error.response?.data?.message || error.message || 'Upload failed' 
          };
        }
      });

      const results = await Promise.all(uploadPromises);
      const successful = results.filter(r => r.success);
      const failed = results.filter(r => !r.success);

      if (successful.length > 0) {
        toast.success(`${successful.length} file(s) uploaded successfully`);
      }

      if (failed.length > 0) {
        toast.error(`${failed.length} file(s) failed to upload`);
        failed.forEach(f => {
          toast.error(`${f.file}: ${f.error}`);
        });
      }

      // Navigate back after successful upload
      if (successful.length > 0 && failed.length === 0) {
        setTimeout(() => {
          // Redirect to classification folder if a classification was selected
          if (selectedCategory) {
            navigate(`/documents/folder/${selectedCategory}`);
          } else if (folderId) {
            navigate(`/documents/folder/${folderId}`);
          } else {
            navigate('/documents/mydocuments');
          }
        }, 2000);
      }
    } catch (error) {
      console.error('Error during upload:', error);
      toast.error('Failed to upload files');
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    if (folderId) {
      navigate(`/documents/folder/${folderId}`);
    } else {
      navigate('/documents/mydocuments');
    }
  };

  const handleDuplicateChoice = (fileName, choice) => {
    setDuplicateChoices(prev => ({
      ...prev,
      [fileName]: choice
    }));
  };

  const handleDuplicateModalSubmit = () => {
    setShowDuplicateModal(false);
    proceedWithUpload(uploadFiles, duplicateChoices);
  };

  // Show loading state
  if (accessLoading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ height: '50vh' }}>
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  // Show feature locked message if user doesn't have access
  if (!hasDocumentAccess) {
    return (
      <div style={{ padding: '2rem', maxWidth: '800px', margin: '0 auto' }}>
        <div className="container-fluid">
          <div className="row">
            <div className="col-12">
              <div className="card">
                <div className="card-body text-center">
                  <div className="feature-locked">
                    <i className="fas fa-lock text-warning" style={{ fontSize: '3rem', marginBottom: '1rem' }}></i>
                    <h4 className="text-muted mb-3">Uploading Documents Not Available</h4>
                    <p className="text-muted mb-4">
                      Uploading documents is not available in your current subscription plan or you have exceeded your storage limit.
                      Upgrade your plan or free up space to access this feature.
                    </p>
                    <div className="d-flex gap-3 justify-content-center">
                      <button
                        className="btn btn-outline-secondary"
                        onClick={() => navigate('/documents/mydocuments')}
                      >
                        <i className="fas fa-arrow-left me-2"></i>
                        Back to Documents
                      </button>
                      <button
                        className="btn btn-primary"
                        onClick={() => navigate('/user-subscription')}
                      >
                        <i className="fas fa-crown me-2"></i>
                        Upgrade Plan
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div>
      <ToastContainer />
      <div className="d-flex align-items-center mb-3">
        <button
          className="back-button"
          onClick={handleCancel}
        >
          <FiArrowLeft style={{ marginRight: '0.5rem' }} />
          Back
        </button>
      </div>
      
      <Row>
        <Col lg={12}>
          <Card>
            <Card.Header>
              <div className="d-flex justify-content-between align-items-center">
                <h4 className="mb-0">
                  <FiUpload className="me-2" />
                  Upload Documents
                </h4>
              </div>
            </Card.Header>
            <Card.Body>
              <DocumentUploadForm
                files={uploadFiles}
                onFilesChange={setUploadFiles}
                selectedCategory={selectedCategory}
                onCategoryChange={setSelectedCategory}
                folderId={folderId}
                categories={documentClassifications}
                dynamicFields={dynamicFields}
                onDynamicFieldsChange={setDynamicFields}
                isPrivate={isPrivate}
                onIsPrivateChange={setIsPrivate}
                selectedBranches={selectedBranches}
                onBranchesChange={handleBranchChange}
                selectedDepartments={selectedDepartments}
                onDepartmentsChange={handleDepartmentChange}
                selectedSubDepartments={selectedSubDepartments}
                onSubDepartmentsChange={handleSubDepartmentChange}
                branches={branches}
                departments={departments}
                subDepartments={subDepartments}
                filteredDepartments={filteredDepartments}
                filteredSubDepartments={filteredSubDepartments}
                availableUsers={filteredUsers}
                selectedUsers={selectedUsers}
                onUsersChange={handleUserChange}
                documentTitle={documentTitle}
                onDocumentTitleChange={(v) => { setDocumentTitle(v); setFieldErrors(prev => ({ ...prev, documentTitle: '' })); }}
                documentTitleError={fieldErrors.documentTitle}
                documentRegisteredTo={documentRegisteredTo}
                onDocumentRegisteredToChange={(v) => { setDocumentRegisteredTo(v); setFieldErrors(prev => ({ ...prev, documentRegisteredTo: '' })); }}
                documentRegisteredToError={fieldErrors.documentRegisteredTo}
                documentReferenceNumber={documentReferenceNumber}
                onDocumentReferenceNumberChange={(v) => { setDocumentReferenceNumber(v); setFieldErrors(prev => ({ ...prev, documentReferenceNumber: '' })); }}
                documentReferenceNumberError={fieldErrors.documentReferenceNumber}
                documentIssueDate={documentIssueDate}
                onDocumentIssueDateChange={setDocumentIssueDate}
                documentExpiryDate={documentExpiryDate}
                onDocumentExpiryDateChange={setDocumentExpiryDate}
                remindMeBefore={remindMeBefore}
                onRemindMeBeforeChange={setRemindMeBefore}
                documentIssuingAuthority={documentIssuingAuthority}
                onDocumentIssuingAuthorityChange={(v) => { setDocumentIssuingAuthority(v); setFieldErrors(prev => ({ ...prev, documentIssuingAuthority: '' })); }}
                documentIssuingAuthorityError={fieldErrors.documentIssuingAuthority}
                otherComments={otherComments}
                onOtherCommentsChange={(v) => { setOtherComments(v); setFieldErrors(prev => ({ ...prev, otherComments: '' })); }}
                otherCommentsError={fieldErrors.otherComments}
                duplicateFiles={duplicateFiles}
                checkingDuplicates={checkingDuplicates}
                linkToStaff={linkToStaff}
                onLinkToStaffChange={setLinkToStaff}
                staffId={staffId}
                onStaffIdChange={setStaffId}
                staffList={staffList}
                linkToVehicle={linkToVehicle}
                onLinkToVehicleChange={setLinkToVehicle}
                vehicleId={vehicleId}
                onVehicleIdChange={setVehicleId}
                vehicleList={vehicleList}
              />
            </Card.Body>
            <Card.Footer>
              <div className="d-flex justify-content-end gap-2">
                <Button className="doc-form-cancel-btn" onClick={handleCancel}>
                  Cancel
                </Button>
                <Button 
                  className="doc-form-submit-btn"
                  onClick={handleFileUpload}
                  disabled={loading || uploadFiles.length === 0}
                >
                  {loading ? 'Uploading...' : 'Upload File'}
                </Button>
              </div>
            </Card.Footer>
          </Card>
        </Col>
      </Row>

      {/* Duplicate Modal */}
      {showDuplicateModal && (
        <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }} onClick={() => setShowDuplicateModal(false)}>
          <div className="modal-dialog modal-dialog-centered" onClick={(e) => e.stopPropagation()}>
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Duplicate Files Found</h5>
                <button type="button" className="btn-close" onClick={() => setShowDuplicateModal(false)}></button>
              </div>
              <div className="modal-body">
                <p>Some files with the same names already exist. Choose an action for each:</p>
                {duplicateFiles.map(dup => (
                  <div key={dup.name} className="mb-3">
                    <strong>{dup.name}</strong>
                    <div className="mt-2">
                      <Button
                        size="sm"
                        variant={duplicateChoices[dup.name] === 'replace' ? 'primary' : 'outline-primary'}
                        onClick={() => handleDuplicateChoice(dup.name, 'replace')}
                        className="me-2"
                      >
                        Replace
                      </Button>
                      <Button
                        size="sm"
                        variant={duplicateChoices[dup.name] === 'copy' ? 'primary' : 'outline-primary'}
                        onClick={() => handleDuplicateChoice(dup.name, 'copy')}
                      >
                        Create Copy
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
              <div className="modal-footer">
                <Button className="doc-form-cancel-btn" onClick={() => setShowDuplicateModal(false)}>
                  Cancel
                </Button>
                <Button className="doc-form-submit-btn" onClick={handleDuplicateModalSubmit}>
                  Continue Upload
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      <InternalFooter />
    </div>
  );
};

export default AddDocument;

